import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";

import { useWorklistQuery } from "../../hooks/useLabelingQueries";
import { FaChevronRight, FaFileAlt } from "../../utils/icons";

export function ReportingStudyHome() {
  const navigate = useNavigate();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const worklistQuery = useWorklistQuery(projectId);

  const studies = useMemo(() => worklistQuery.data?.studies ?? [], [worklistQuery.data]);
  const aiCount = studies.filter((s) => (s.template_mode ?? "ai") === "ai").length;
  const blankCount = studies.length - aiCount;
  const practiceCount = studies.filter((s) => s.is_practice).length;

  const handleOpen = (caseIndex: number) => {
    navigate(`/labeling?project=${encodeURIComponent(projectId)}&case=${caseIndex}`);
  };

  if (worklistQuery.isLoading) {
    return (
      <div className="h-full overflow-auto flex items-center justify-center page-premium-alt">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <span className="text-fg-muted text-sm">Loading worklist...</span>
        </div>
      </div>
    );
  }

  if (worklistQuery.error || !worklistQuery.data) {
    return (
      <div className="h-full overflow-auto flex items-center justify-center page-premium-alt">
        <div className="bg-warn-tint border border-warn/30 rounded p-6 max-w-md text-center">
          <div className="text-warn font-medium mb-2">Failed to load worklist</div>
          <div className="text-warn/60 text-sm">
            {String(worklistQuery.error ?? "Unknown error")}
          </div>
        </div>
      </div>
    );
  }

  const worklist = worklistQuery.data;

  return (
    <>
      <Helmet>
        <title>{worklist.name} | a2z</title>
      </Helmet>

      <div className="h-full overflow-auto text-white page-premium">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white">{worklist.name}</h1>
            <div className="flex items-center gap-4 mt-3 text-xs text-fg-muted">
              <span>{studies.length} cases</span>
              <span className="text-fg-muted">|</span>
              <span>{aiCount} with AI template</span>
              <span className="text-fg-muted">|</span>
              <span>{blankCount} blank</span>
              {practiceCount > 0 && (
                <>
                  <span className="text-fg-muted">|</span>
                  <span>{practiceCount} untimed practice</span>
                </>
              )}
            </div>
          </div>

          <div className="panel-premium rounded border border-border/50 divide-y divide-border/50">
            {studies.map((study, idx) => {
              const mode = study.template_mode ?? "ai";
              // Canonical-built worklists emit empty patient_id/accession because
              // canonicalize strips both. Fall back to study_description so the
              // row stays informative; otherwise the upper label would render as
              // `· `. Pre-canonical worklists (smoke/legacy) still have these
              // populated and take the first branch.
              const primaryLabel =
                study.patient_id || study.accession
                  ? `${study.patient_id} · ${study.accession}`
                  : (study.study_description ?? study.study_instance_uid);
              return (
                <button
                  key={study.case_id}
                  onClick={() => handleOpen(idx)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/2 transition text-left"
                >
                  <div className="w-8 text-fg-muted text-sm font-mono">{idx + 1}</div>
                  <FaFileAlt className="w-4 h-4 text-fg-muted shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-fg truncate">{primaryLabel}</div>
                    <div className="text-xs text-fg-muted truncate">{study.study_instance_uid}</div>
                  </div>
                  <span
                    className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wide rounded border ${
                      study.is_practice
                        ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                        : "bg-emerald-500/10 text-emerald-300 border-emerald-500/25"
                    }`}
                  >
                    {study.is_practice ? "Untimed" : "Timed"}
                  </span>
                  <span
                    className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wide rounded ${
                      mode === "ai"
                        ? "bg-primary/15 text-signal-text-tint"
                        : "bg-surface-2 text-fg-muted"
                    }`}
                  >
                    {mode === "ai" ? "AI template" : "Blank"}
                  </span>
                  <FaChevronRight className="w-3 h-3 text-fg-muted" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
